import type { CreateWorkerInput, UpdateWorkerInput, WorkerProfile } from '../index.js'
import { EMPLOYMENT_TYPES, EMPLOYMENT_TYPE_LABELS, type EmploymentType } from '../../core/index.js'
import type { WorkerRole, WorkerTier } from '../../core/index.js'
import { TIER_LABELS } from '../../core/index.js'
import { WORKER_ROLE_LABELS } from '../../core/index.js'
import { Field } from '../../core/index.js'
import { Switch } from '../../core/index.js'
import { credentialsFromField, credentialsToField, unitListFromField, type WorkerCredential, type WorkerUnits } from '../../core/index.js'
import { PhoneInput } from '../../core/index.js'
import { RevealableAddress } from '../../messages/index.js'

const ROLES: readonly WorkerRole[] = ['RN', 'CNA', 'LPN']
const TIERS: readonly WorkerTier[] = ['probationary', 'bronze', 'silver', 'gold']

/** What the New worker form holds, as typed - numbers stay text until submit so a blank stays a blank. */
export interface WorkerFormValues {
  firstName: string
  lastName: string
  role: WorkerRole
  employmentType: EmploymentType
  tier: WorkerTier
  score: string
  hireDate: string
  /** `YYYY-MM-DD`, or blank for someone still at the facility. */
  leaveDate: string
  callOuts: string
  chartingPct: string
  /** Days of PTO the worker has, as HR holds it; blank when not recorded. */
  ptoBalanceDays: string
  /** Weekly hours the worker is contracted for; blank when not recorded. */
  contractedHoursPerWeek: string
  /** The home unit; blank for none recorded (phase D). */
  primaryUnit: string
  /** Units the worker floats to, comma or semicolon separated. */
  secondaryUnits: string
  /** Credentials as the roster file writes them: "BLS:2027-03-01; ACLS". */
  credentials: string
  /** Whether the facility lets the worker enroll for Clinical Bridge days (kept on their Bridge record, not the profile). */
  bridgeEligible: boolean
  /** A mobile number to enter for the worker, any usual spelling; blank sends nothing (SMS plan 3.5). It goes to their contacts record, never the profile. */
  mobile: string
  /** On the edit form: remove the number the facility entered, where the worker has not agreed to it. */
  clearMobile: boolean
}

export const EMPTY_WORKER_FORM: WorkerFormValues = { firstName: '', lastName: '', role: 'RN', employmentType: 'full_time', tier: 'probationary', score: '0', hireDate: '', leaveDate: '', callOuts: '', chartingPct: '', ptoBalanceDays: '', contractedHoursPerWeek: '', primaryUnit: '', secondaryUnits: '', credentials: '', bridgeEligible: false, mobile: '', clearMobile: false }

/** The fields of a worker a person may change after the fact - the name, role, hire date, readings and Bridge eligibility - as typed. */
export type WorkerDetailValues = Pick<WorkerFormValues, 'firstName' | 'lastName' | 'role' | 'employmentType' | 'hireDate' | 'leaveDate' | 'callOuts' | 'chartingPct' | 'ptoBalanceDays' | 'contractedHoursPerWeek' | 'primaryUnit' | 'secondaryUnits' | 'credentials' | 'bridgeEligible' | 'mobile' | 'clearMobile'>

/**
 * The worker's one name, "First Last", as the roster stores and shows it.
 * The forms ask for the two parts separately, as every other New form does.
 */
export function workerFullName(form: Pick<WorkerFormValues, 'firstName' | 'lastName'>): string {
  return [form.firstName.trim(), form.lastName.trim()].filter((part) => part !== '').join(' ')
}

/** Whether both parts of the name are filled in. */
export function hasWorkerName(form: Pick<WorkerFormValues, 'firstName' | 'lastName'>): boolean {
  return form.firstName.trim() !== '' && form.lastName.trim() !== ''
}

/** A stored name split for the form: the first word, then the rest (so "Mary Ann Lee" reads as Mary / Ann Lee, to be corrected by hand). */
function splitName(name: string): { firstName: string; lastName: string } {
  const [firstName = '', ...rest] = name.trim().split(/\s+/)
  return { firstName, lastName: rest.join(' ') }
}

/** A blank optional number is "not recorded", which the API stores as null. */
function optionalNumber(text: string): number | null {
  return text.trim() === '' ? null : Number(text)
}

/** A stored value as the form shows it: null reads as blank. */
function asText(value: number | string | null): string {
  return value === null ? '' : String(value)
}

/** A stored profile as the edit form starts; the Bridge eligibility comes from the worker's Bridge record. */
export function workerDetailValues(profile: WorkerProfile, bridgeEligible: boolean): WorkerDetailValues {
  return { ...splitName(profile.name), role: profile.role, employmentType: profile.employmentType ?? 'full_time', hireDate: profile.hireDate ?? '', leaveDate: profile.leaveDate ?? '', callOuts: asText(profile.callOuts), chartingPct: asText(profile.chartingPct), ptoBalanceDays: asText(profile.ptoBalanceDays ?? null), contractedHoursPerWeek: asText(profile.contractedHoursPerWeek ?? null), primaryUnit: profile.units?.primary ?? '', secondaryUnits: (profile.units?.secondary ?? []).join(', '), credentials: credentialsToField(profile.credentials), bridgeEligible, mobile: '', clearMobile: false }
}

/** The mobile field as the API takes it: a typed number, null to clear, or nothing. */
function mobileToInput(form: Pick<WorkerDetailValues, 'mobile' | 'clearMobile'>): string | null | undefined {
  if (form.clearMobile) {
    return null
  }
  return form.mobile.trim() === '' ? undefined : form.mobile.trim()
}

/** The details as the API takes them; the mobile number only where typed or cleared. */
function detailsToInput(form: WorkerDetailValues): Required<Omit<UpdateWorkerInput, 'mobile'>> & Pick<UpdateWorkerInput, 'mobile'> {
  const mobile = mobileToInput(form)
  return {
    ...(mobile === undefined ? {} : { mobile }),
    name: workerFullName(form),
    role: form.role,
    employmentType: form.employmentType,
    hireDate: form.hireDate === '' ? null : form.hireDate,
    leaveDate: form.leaveDate === '' ? null : form.leaveDate,
    callOuts: optionalNumber(form.callOuts),
    chartingPct: optionalNumber(form.chartingPct),
    ptoBalanceDays: optionalNumber(form.ptoBalanceDays),
    contractedHoursPerWeek: optionalNumber(form.contractedHoursPerWeek),
    units: unitsFromForm(form),
    credentials: credentialsOrNull(form.credentials),
    bridgeEligible: form.bridgeEligible,
  }
}

/** The two unit fields as the API takes them: both blank is "any unit", which is null. */
function unitsFromForm(form: Pick<WorkerDetailValues, 'primaryUnit' | 'secondaryUnits'>): WorkerUnits | null {
  const primary = form.primaryUnit.trim() === '' ? null : form.primaryUnit.trim()
  const secondary = unitListFromField(form.secondaryUnits).filter((unit) => unit !== primary)
  return primary === null && secondary.length === 0 ? null : { primary, secondary }
}

function credentialsOrNull(text: string): WorkerCredential[] | null {
  const parsed = credentialsFromField(text)
  return parsed.length === 0 ? null : parsed
}

/** Whether two field values are the same, objects and lists compared by what they hold. */
function sameValue(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

/** Only what differs from what is stored, so a save sends nothing it did not change; empty when nothing did. */
export function workerDetailChanges(profile: WorkerProfile, bridgeEligible: boolean, form: WorkerDetailValues): UpdateWorkerInput {
  const next = detailsToInput(form)
  // The profile never carries the number, so a typed or cleared one is always a change.
  const stored = { ...profile, bridgeEligible, mobile: undefined }
  const changed = (Object.keys(next) as (keyof typeof next)[]).filter((key) => key === 'mobile' || !sameValue(next[key], stored[key]))
  return Object.fromEntries(changed.map((key) => [key, next[key]])) as UpdateWorkerInput
}

/**
 * An id for a worker added by hand: nobody reads it, so it is made here in
 * the server's own form (`w-` and twelve hex digits). Sent rather than left
 * to the server so an API from before the id became optional still takes it.
 */
function newWorkerId(): string {
  return `w-${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`
}

/** The form as the API takes it. */
export function workerFormToInput(form: WorkerFormValues): CreateWorkerInput {
  const { mobile, ...details } = detailsToInput(form)
  return { workerId: newWorkerId(), ...details, tier: form.tier, score: Number(form.score), ...(mobile ? { mobile } : {}) }
}

interface MobileFieldProps extends Pick<DetailFieldProps, 'form' | 'onChange' | 'inputClass' | 'idPrefix'> {
  /** The number's state from the worker's contacts, on the edit form: described, with whether the worker has agreed. */
  current?: { workerId: string; contactId: string; address: string; awaitingAgreement: boolean; optedOut: boolean } | null
}

/** The worker's mobile number as staff enter it (SMS plan 3.5): the worker is asked to agree before anything is texted. */
export function WorkerMobileField({ form, onChange, inputClass, idPrefix, current = null }: MobileFieldProps) {
  const ownedByWorker = current !== null && !current.awaitingAgreement
  const state = current === null ? null : current.awaitingAgreement ? 'waiting for the worker to agree' : current.optedOut ? "the worker's own; they replied STOP" : "the worker's own, agreed to in their app"
  return (
    <Field label="Mobile" htmlFor={`${idPrefix}-mobile`} hint={ownedByWorker ? 'A number the worker agreed to themselves is theirs: only they change it, on their Notifications card.' : 'The worker is asked to agree before anything is texted: one invitation goes to the number, and they reply YES or agree in the app.'}>
      {current && state && (
        <p className={`mb-1 text-[11px] ${ownedByWorker ? 'text-green' : 'text-gold'}`}>
          <RevealableAddress workerId={current.workerId} contact={{ contactId: current.contactId, channel: 'sms', address: current.address }} /> · {state}
        </p>
      )}
      {!ownedByWorker && (
        <div className="flex flex-wrap items-center gap-3">
          <PhoneInput id={`${idPrefix}-mobile`} autoComplete="off" className={inputClass} value={form.mobile} disabled={form.clearMobile} onChange={(mobile) => onChange({ mobile })} />
          {current !== null && (
            <label className="flex items-center gap-2 text-[11px] text-ink-mid">
              <Switch checked={form.clearMobile} onChange={(checked) => onChange({ clearMobile: checked, ...(checked ? { mobile: '' } : {}) })} label="Remove the number" />
              Remove the number
            </label>
          )}
        </div>
      )}
    </Field>
  )
}

/** Staff's say on Clinical Bridge (plan D2): the one switch that lets a worker enroll for days they would cover a gap. */
export function WorkerBridgeField({ form, onChange, idPrefix }: Pick<DetailFieldProps, 'form' | 'onChange' | 'idPrefix'>) {
  return (
    <Field label="Clinical Bridge" htmlFor={`${idPrefix}-bridge`} hint="Eligible workers can enroll for days they would cover a call-out. Nobody is eligible until you say so.">
      <div id={`${idPrefix}-bridge`} className="flex items-center gap-3 py-1">
        <Switch checked={form.bridgeEligible} onChange={(checked) => onChange({ bridgeEligible: checked })} label="Eligible for Clinical Bridge" />
        <span className="text-sm text-ink">{form.bridgeEligible ? 'Eligible' : 'Not eligible'}</span>
      </div>
    </Field>
  )
}

interface DetailFieldProps {
  form: WorkerDetailValues
  onChange: (changes: Partial<WorkerDetailValues>) => void
  inputClass: string
  /** Prefixes every control id, so two forms on one page never share one. */
  idPrefix: string
  autoFocus?: boolean
}

/** The worker's first and last name, saved as one "First Last" name and shown whole in every roster row. */
export function WorkerNameField({ form, onChange, inputClass, idPrefix, autoFocus = false }: DetailFieldProps) {
  return (
    <>
      <Field label="First name" htmlFor={`${idPrefix}-first-name`} required>
        <input id={`${idPrefix}-first-name`} className={inputClass} value={form.firstName} onChange={(event) => onChange({ firstName: event.target.value })} autoComplete="off" required autoFocus={autoFocus} />
      </Field>
      <Field label="Last name" htmlFor={`${idPrefix}-last-name`} required hint="Names can repeat on a roster: two people of the same name are two workers.">
        <input id={`${idPrefix}-last-name`} className={inputClass} value={form.lastName} onChange={(event) => onChange({ lastName: event.target.value })} autoComplete="off" required />
      </Field>
    </>
  )
}

export function WorkerRoleField({ form, onChange, inputClass, idPrefix }: DetailFieldProps) {
  return (
    <Field label="Role" htmlFor={`${idPrefix}-role`} required>
      <select id={`${idPrefix}-role`} className={inputClass} value={form.role} onChange={(event) => onChange({ role: event.target.value as WorkerRole })} required>
        {ROLES.map((role) => (
          <option key={role} value={role}>
            {role} · {WORKER_ROLE_LABELS[role]}
          </option>
        ))}
      </select>
    </Field>
  )
}

/** Full time, part time, PRN or agency: the facility's scheduling rules keep different caps for each. */
export function WorkerEmploymentField({ form, onChange, inputClass, idPrefix }: DetailFieldProps) {
  return (
    <Field label="Employment" htmlFor={`${idPrefix}-employment`} required hint="The facility's scheduling rules set the hours, High priority days and PTO days for each type and tier, on the Configuration page's Scheduling tab.">
      <select id={`${idPrefix}-employment`} className={inputClass} value={form.employmentType} onChange={(event) => onChange({ employmentType: event.target.value as EmploymentType })} required>
        {EMPLOYMENT_TYPES.map((type) => (
          <option key={type} value={type}>
            {EMPLOYMENT_TYPE_LABELS[type]}
          </option>
        ))}
      </select>
    </Field>
  )
}

/** The hire and leave dates and the facility's two readings, two to a row. */
export function WorkerReadingsFields({ form, onChange, inputClass, idPrefix }: DetailFieldProps) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label="Hire date" htmlFor={`${idPrefix}-hire-date`} hint="Read by the new-hire probation rule. Leave blank if not known.">
        <input id={`${idPrefix}-hire-date`} type="date" className={inputClass} value={form.hireDate} onChange={(event) => onChange({ hireDate: event.target.value })} />
      </Field>
      <Field label="Leave date" htmlFor={`${idPrefix}-leave-date`} hint="Their last day, once they have left. Leave blank for someone still here.">
        <input id={`${idPrefix}-leave-date`} type="date" className={inputClass} min={form.hireDate || undefined} value={form.leaveDate} onChange={(event) => onChange({ leaveDate: event.target.value })} />
      </Field>
      <Field label="Call-outs this cycle" htmlFor={`${idPrefix}-call-outs`} hint="A count. Blank means nothing reported yet, which is not a zero.">
        <input id={`${idPrefix}-call-outs`} type="number" inputMode="numeric" min={0} step={1} className={inputClass} value={form.callOuts} onChange={(event) => onChange({ callOuts: event.target.value })} />
      </Field>
      <Field label="Charting %" htmlFor={`${idPrefix}-charting`} hint="0 to 100. Blank means nothing reported yet.">
        <input id={`${idPrefix}-charting`} type="number" inputMode="decimal" min={0} max={100} step="any" className={inputClass} value={form.chartingPct} onChange={(event) => onChange({ chartingPct: event.target.value })} />
      </Field>
      <Field label="PTO balance (days)" htmlFor={`${idPrefix}-pto-balance`} hint="As HR holds it. The worker's shift preferences cannot ask for more PTO than this; blank means not recorded and nothing is refused.">
        <input id={`${idPrefix}-pto-balance`} type="number" inputMode="decimal" min={0} max={365} step={0.5} className={inputClass} value={form.ptoBalanceDays} onChange={(event) => onChange({ ptoBalanceDays: event.target.value })} />
      </Field>
      <Field label="Contracted hours a week" htmlFor={`${idPrefix}-contracted-hours`} hint="As HR holds it (0.9 FTE is 36). The scheduling rules take it as the worker's own monthly floor when the facility reads it; blank means the tier's floor applies.">
        <input id={`${idPrefix}-contracted-hours`} type="number" inputMode="decimal" min={0} max={168} step={0.5} className={inputClass} value={form.contractedHoursPerWeek} onChange={(event) => onChange({ contractedHoursPerWeek: event.target.value })} />
      </Field>
    </div>
  )
}

/** The units the worker may work and the credentials they hold (phase D): the scheduling rules draft and offer shifts by them. */
export function WorkerStaffingFields({ form, onChange, inputClass, idPrefix }: DetailFieldProps) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label="Home unit" htmlFor={`${idPrefix}-primary-unit`} hint="As published shifts name it (2-South). Leave both unit fields blank and the worker may be scheduled on any unit.">
        <input id={`${idPrefix}-primary-unit`} className={inputClass} value={form.primaryUnit} onChange={(event) => onChange({ primaryUnit: event.target.value })} />
      </Field>
      <Field label="Floats to" htmlFor={`${idPrefix}-secondary-units`} hint="Other units the worker may work, separated by commas. A swap that moves a shift off someone's home unit waits for a manager.">
        <input id={`${idPrefix}-secondary-units`} className={inputClass} value={form.secondaryUnits} onChange={(event) => onChange({ secondaryUnits: event.target.value })} />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Credentials" htmlFor={`${idPrefix}-credentials`} hint="Each code with the day it lapses, as the roster file writes them: BLS:2027-03-01; ACLS. A worker without a credential the role or shift requires, or with one lapsed by the shift's day, is not scheduled on it.">
          <input id={`${idPrefix}-credentials`} className={`${inputClass} font-mono`} value={form.credentials} onChange={(event) => onChange({ credentials: event.target.value })} placeholder="BLS:2027-03-01; ACLS" />
        </Field>
      </div>
    </div>
  )
}

interface WorkerFormFieldsProps {
  form: WorkerFormValues
  onChange: (changes: Partial<WorkerFormValues>) => void
  inputClass: string
  autoFocus?: boolean
}

/**
 * Every field of a worker record a person can set - the required three and
 * the optional standing and readings an import file could carry - shared by
 * the operator's and the admin's New worker forms so a worker is described
 * the same way wherever it is added.
 */
export function WorkerFormFields({ form, onChange, inputClass, autoFocus = false }: WorkerFormFieldsProps) {
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <WorkerNameField form={form} onChange={onChange} inputClass={inputClass} idPrefix="new-worker" autoFocus={autoFocus} />
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <WorkerRoleField form={form} onChange={onChange} inputClass={inputClass} idPrefix="new-worker" />
        <WorkerEmploymentField form={form} onChange={onChange} inputClass={inputClass} idPrefix="new-worker" />
        <Field label="Tier" htmlFor="new-worker-tier" hint="Held until a period closes. New hires start probationary.">
          <select id="new-worker-tier" className={inputClass} value={form.tier} onChange={(event) => onChange({ tier: event.target.value as WorkerTier })}>
            {TIERS.map((tier) => (
              <option key={tier} value={tier}>
                {TIER_LABELS[tier]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Score" htmlFor="new-worker-score" hint="0 to 100. The running score starts here.">
          <input id="new-worker-score" type="number" inputMode="decimal" min={0} max={100} step="any" className={inputClass} value={form.score} onChange={(event) => onChange({ score: event.target.value })} required />
        </Field>
      </div>

      <WorkerReadingsFields form={form} onChange={onChange} inputClass={inputClass} idPrefix="new-worker" />
      <WorkerStaffingFields form={form} onChange={onChange} inputClass={inputClass} idPrefix="new-worker" />
      <WorkerBridgeField form={form} onChange={onChange} idPrefix="new-worker" />
      <WorkerMobileField form={form} onChange={onChange} inputClass={inputClass} idPrefix="new-worker" />

      <p className="text-[11px] text-ink-mid">The score reset and bid-window dates are set by the API to the first of next month, as they are for a worker arriving through the onboarding event.</p>
    </>
  )
}
